import React from 'react';
import { useForm } from 'react-hook-form';
import { useImperativeHandle } from 'react';
import toast from 'react-hot-toast';
import {
  FormControl,
  FormLabel,
  FormErrorMessage,
  Input,
  Textarea,
  VStack,
  Grid,
  GridItem,
  Switch,
  FormHelperText,
  Box,
  Text,
  UnorderedList,
  ListItem,
  Button,
  HStack,
} from '@chakra-ui/react';
import JalaliDatePicker from '../JalaliDatePicker';
import { jalaliToGregorian } from '../../utils/timeUtils';
import moment from 'moment-jalaali';

/**
 * فرم افزودن/ویرایش مشاور
 * @param {Object} props - props کامپوننت
 * @param {Object} props.consultant - اطلاعات مشاور برای ویرایش (اختیاری)
 * @param {Function} props.onSubmit - تابع ارسال فرم
 * @param {boolean} props.isLoading - وضعیت بارگذاری
 */
const ConsultantForm = React.forwardRef(({ consultant, onSubmit, isLoading }, ref) => {
  const {
    register,
    handleSubmit,
    formState: { errors },
    watch,
    setValue,
    getValues,
    trigger,
  } = useForm({
    defaultValues: {
      firstName: consultant?.firstName || '',
      lastName: consultant?.lastName || '',
      email: consultant?.email || '',
      phone: consultant?.phone || '',
      specialty: consultant?.specialty || '',
      licenseNumber: consultant?.licenseNumber || '',
      experience: consultant?.experience || '',
      education: consultant?.education || '',
      bio: consultant?.bio || '',
      hourlyRate: consultant?.hourlyRate || 0,
      deposit:consultant?.deposit,
      sessionDuration: consultant?.sessionDuration || 60,
      showDuration:consultant?.showDuration || 45,
      appointmentEndDate: consultant?.appointmentEndDate
        ? moment(consultant.appointmentEndDate).format('jYYYY/jMM/jDD')
        : '',
      paymentLink: consultant?.paymentLink || '',
      active: consultant?.active !== false,
    },
    mode: 'onSubmit', // اعتبارسنجی هنگام submit
  });

  // تابع تبدیل نام فیلد به برچسب فارسی
  const getFieldLabel = (fieldName) => {
    const labels = {
      firstName: 'نام',
      lastName: 'نام خانوادگی',
      email: 'ایمیل',
      phone: 'شماره تماس',
      specialty: 'تخصص',
      licenseNumber: 'شماره پروانه',
      experience: 'سابقه کاری',
      education: 'تحصیلات',
      bio: 'بیوگرافی',
      hourlyRate: 'نرخ ساعتی',
      sessionDuration: 'طول جلسه',
      appointmentEndDate: 'تاریخ پایان نوبت‌دهی',
      showDuration:'تایم نمایشی',
      deposit:'نرخ بیعانه',
      paymentLink: 'لینک پرداخت بیعانه'
    };
    return labels[fieldName] || fieldName;
  };

  const handleFormSubmit = async (data) => {
    
    // Trigger validation برای همه فیلدها
    const isValid = await trigger();

    
    // اگر خطایی وجود دارد، فرم ارسال نمی‌شود
    if (!isValid) {
      console.log('Form has validation errors, not submitting');
      toast.error('لطفاً خطاهای فرم را برطرف کنید');
      return;
    }
    
    // تبدیل تاریخ شمسی به میلادی
    if (data.appointmentEndDate) {
      const gregorianDate = jalaliToGregorian(data.appointmentEndDate);
      if (gregorianDate) {
        data.appointmentEndDate = gregorianDate.toISOString().split('T')[0];
      } else {
        data.appointmentEndDate = '';
      }
    }
    
    // اعتبارسنجی نهایی قبل از ارسال
    const customErrors = [];
    
    // بررسی تکراری نبودن شماره تماس (اگر در حالت ویرایش نیستیم)
    if (!consultant && data.phone) {
      // اینجا می‌توانید API call برای بررسی تکراری بودن شماره تماس اضافه کنید
    }
    
    // بررسی تکراری نبودن شماره پروانه
    if (!consultant && data.licenseNumber) {
      // اینجا می‌توانید API call برای بررسی تکراری بودن شماره پروانه اضافه کنید
    }
    
    if (customErrors.length > 0) {
      // نمایش خطاها
      return;
    }
    
    onSubmit(data);
  };

  // قرار دادن getValues و trigger در ref
  useImperativeHandle(ref, () => ({
    getValues: () => getValues(),
    trigger: async () => {
      const isValid = await trigger();
      return { isValid, errors };
    }, // برای trigger کردن validation
    isValid: () => Object.keys(errors).length === 0, // بررسی معتبر بودن فرم
    getErrors: () => errors, // دریافت خطاها
    validateAndSubmit: async () => {
      const isValid = await trigger();
      if (isValid) {
        const data = getValues();
        onSubmit(data);
      }
      return { isValid, errors };
    }
  }));

  return (
    <form ref={ref} onSubmit={handleSubmit(handleFormSubmit)}>
      <VStack spacing={4} align="stretch">
        {/* نمایش خطاهای کلی فرم */}
        {Object.keys(errors).length > 0 && (
          <Box p={3} bg="red.50" border="1px" borderColor="red.200" borderRadius="md">
            <Text color="red.600" fontSize="sm" fontWeight="medium">
              لطفاً خطاهای زیر را برطرف کنید:
            </Text>
            <UnorderedList mt={2} color="red.600" fontSize="sm">
              {Object.entries(errors).map(([fieldName, error], index) => (
                <ListItem key={index}>
                  <Text fontWeight="medium">{getFieldLabel(fieldName)}: </Text>
                  {error.message}
                </ListItem>
              ))}
            </UnorderedList>
          </Box>
        )}
        {/* نام و نام خانوادگی */}
        <Grid templateColumns="repeat(2, 1fr)" gap={4}>
          <GridItem>
            <FormControl isInvalid={errors.firstName}>
              <FormLabel>نام *</FormLabel>
              <Input
                {...register('firstName', {
                  required: 'نام الزامی است',
                  minLength: { value: 2, message: 'نام باید حداقل 2 کاراکتر باشد' },
                  maxLength: { value: 50, message: 'نام نمی‌تواند بیشتر از 50 کاراکتر باشد' },
                  pattern: {
                    value: /^[\u0600-\u06FF\s]+$/,
                    message: 'نام باید فقط شامل حروف فارسی باشد'
                  }
                })}
                placeholder="نام مشاور"
              />
              <FormErrorMessage>
                {errors.firstName && errors.firstName.message}
              </FormErrorMessage>
            </FormControl>
          </GridItem>
          <GridItem>
            <FormControl isInvalid={errors.lastName}>
              <FormLabel>نام خانوادگی *</FormLabel>
              <Input
                {...register('lastName', {
                  required: 'نام خانوادگی الزامی است',
                  minLength: { value: 2, message: 'نام خانوادگی باید حداقل 2 کاراکتر باشد' },
                  maxLength: { value: 50, message: 'نام خانوادگی نمی‌تواند بیشتر از 50 کاراکتر باشد' },
                  pattern: {
                    value: /^[\u0600-\u06FF\s]+$/,
                    message: 'نام خانوادگی باید فقط شامل حروف فارسی باشد'
                  }
                })}
                placeholder="نام خانوادگی مشاور"
              />
              <FormErrorMessage>
                {errors.lastName && errors.lastName.message}
              </FormErrorMessage>
            </FormControl>
          </GridItem>
        </Grid>

        {/* ایمیل و شماره تماس */}
        <Grid templateColumns="repeat(2, 1fr)" gap={4}>
          <GridItem>
            <FormControl isInvalid={errors.email}>
              <FormLabel>ایمیل</FormLabel>
              <Input
                {...register('email', {
                  pattern: {
                    value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                    message: 'ایمیل معتبر نیست',
                  },
                  maxLength: { value: 100, message: 'ایمیل نمی‌تواند بیشتر از 100 کاراکتر باشد' },
                })}
                placeholder="example@email.com"
              />
              <FormErrorMessage>
                {errors.email && errors.email.message}
              </FormErrorMessage>
            </FormControl>
          </GridItem>
          <GridItem>
            <FormControl isInvalid={errors.phone}>
              <FormLabel>شماره تماس *</FormLabel>
              <Input
                {...register('phone', {
                  required: 'شماره تماس الزامی است',
                  pattern: {
                    value: /^09\d{9}$/,
                    message: 'شماره تماس باید با 09 شروع شود و 11 رقم باشد',
                  },
                  validate: (value) => {
                    if (value && value.length !== 11) {
                      return 'شماره تماس باید دقیقاً 11 رقم باشد';
                    }
                    return true;
                  }
                })}
                placeholder="09120000000"
                maxLength={11}
              />
              <FormErrorMessage>
                {errors.phone && errors.phone.message}
              </FormErrorMessage>
            </FormControl>
          </GridItem>
        </Grid>


        {/* تخصص و شماره پروانه */}
        <Grid templateColumns="repeat(2, 1fr)" gap={4}>
          <GridItem>
            <FormControl isInvalid={errors.specialty}>
              <FormLabel>تخصص</FormLabel>
              <Input
                {...register('specialty', {
                  validate: (value) => {
                    if (value && value.trim() !== '') {
                      if (value.length < 3) {
                        return 'تخصص باید حداقل 3 کاراکتر باشد';
                      }
                      if (value.length > 100) {
                        return 'تخصص نمی‌تواند بیشتر از 100 کاراکتر باشد';
                      }
                      if (!/^[\u0600-\u06FF\s\-\(\)]+$/.test(value)) {
                        return 'تخصص باید فقط شامل حروف فارسی، فاصله، خط تیره و پرانتز باشد';
                      }
                    }
                    return true;
                  }
                })}
                placeholder="تخصص مشاور"
              />
              <FormErrorMessage>
                {errors.specialty && errors.specialty.message}
              </FormErrorMessage>
            </FormControl>
          </GridItem>
          <GridItem>
            <FormControl isInvalid={errors.licenseNumber}>
              <FormLabel>شماره پروانه</FormLabel>
              <Input
                {...register('licenseNumber', {
                  validate: (value) => {
                    if (value && value.trim() !== '') {
                      if (value.length < 5) {
                        return 'شماره پروانه باید حداقل 5 کاراکتر باشد';
                      }
                      if (value.length > 20) {
                        return 'شماره پروانه نمی‌تواند بیشتر از 20 کاراکتر باشد';
                      }
                      if (!/^[0-9\-]+$/.test(value)) {
                        return 'شماره پروانه باید فقط شامل اعداد و خط تیره باشد';
                      }
                    }
                    return true;
                  }
                })}
                placeholder="شماره پروانه مشاوره"
              />
              <FormErrorMessage>
                {errors.licenseNumber && errors.licenseNumber.message}
              </FormErrorMessage>
            </FormControl>
          </GridItem>
        </Grid>

        {/* سابقه و تحصیلات */}
        <Grid templateColumns="repeat(2, 1fr)" gap={4}>
          <GridItem>
            <FormControl isInvalid={errors.experience}>
              <FormLabel>سابقه کاری</FormLabel>
              <Input
                {...register('experience', {
                  maxLength: { value: 200, message: 'سابقه کاری نمی‌تواند بیشتر از 200 کاراکتر باشد' },
                  pattern: {
                    value: /^[\u0600-\u06FF\s\-\(\)0-9]+$/,
                    message: 'سابقه کاری باید فقط شامل حروف فارسی، اعداد، فاصله، خط تیره و پرانتز باشد'
                  }
                })}
                placeholder="مثال: 5 سال"
              />
              <FormErrorMessage>
                {errors.experience && errors.experience.message}
              </FormErrorMessage>
            </FormControl>
          </GridItem>
          <GridItem>
            <FormControl isInvalid={errors.education}>
              <FormLabel>تحصیلات</FormLabel>
              <Input
                {...register('education', {
                  maxLength: { value: 200, message: 'تحصیلات نمی‌تواند بیشتر از 200 کاراکتر باشد' },
                  pattern: {
                    value: /^[\u0600-\u06FF\s\-\(\)0-9]+$/,
                    message: 'تحصیلات باید فقط شامل حروف فارسی، اعداد، فاصله، خط تیره و پرانتز باشد'
                  }
                })}
                placeholder="مثال: کارشناسی ارشد روانشناسی"
              />
              <FormErrorMessage>
                {errors.education && errors.education.message}
              </FormErrorMessage>
            </FormControl>
          </GridItem>
        </Grid>

        {/* نرخ ساعتی و طول جلسه */}
        <Grid templateColumns="repeat(2, 1fr)" gap={4}>
          <GridItem>
            <FormControl isInvalid={errors.hourlyRate}>
              <FormLabel>نرخ ساعتی (تومان) *</FormLabel>
              <Input
                type="number"
                {...register('hourlyRate', {
                  required: 'نرخ ساعتی الزامی است',
                  min: { value: 0, message: 'نرخ ساعتی نمی‌تواند منفی باشد' },
                  max: { value: 10000000, message: 'نرخ ساعتی نمی‌تواند بیشتر از 10 میلیون تومان باشد' },
                  validate: (value) => {
                    if (value && !Number.isInteger(parseFloat(value))) {
                      return 'نرخ ساعتی باید عدد صحیح باشد';
                    }
                    return true;
                  }
                })}
                placeholder="مبلغ به تومان"
                min={1000}
                
              />
              <FormErrorMessage>
                {errors.hourlyRate && errors.hourlyRate.message}
              </FormErrorMessage>
            </FormControl>
          </GridItem>
          <GridItem>
            <FormControl isInvalid={errors.sessionDuration}>
              <FormLabel>طول هر جلسه (دقیقه) *</FormLabel>
              <Input
                type="number"
                {...register('sessionDuration', {
                  required: 'طول جلسه الزامی است',
                  min: { value: 15, message: 'حداقل طول جلسه 15 دقیقه است' },
                  validate: (value) => {
                    if (value && !Number.isInteger(parseFloat(value))) {
                      return 'طول جلسه باید عدد صحیح باشد';
                    }
                    
                    return true;
                  }
                })}
                placeholder="60"
                min={15}
               
              />
              <FormErrorMessage>
                {errors.sessionDuration && errors.sessionDuration.message}
              </FormErrorMessage>
            </FormControl>
          </GridItem>
          <GridItem>
            <FormControl isInvalid={errors.sessionDuration}>
              <FormLabel>طول  جلسه نمایشی (دقیقه) *</FormLabel>
              <Input
                type="number"
                {...register('showDuration', {
                  required: 'طول جلسه نمایشی الزامی است',
                  min: { value: 15, message: 'حداقل طول جلسه 15 دقیقه است' },
                  validate: (value) => {
                    if (value && !Number.isInteger(parseFloat(value))) {
                      return 'طول جلسه باید عدد صحیح باشد';
                    }
                    
                    return true;
                  }
                })}
                placeholder="45"
                min={15}
               
              />
              <FormErrorMessage>
                {errors.showDuration && errors.showDuration.message}
              </FormErrorMessage>
            </FormControl>
          </GridItem>
          <GridItem>
            <FormControl isInvalid={errors.deposit}>
              <FormLabel>نرخ بیعانه (تومان) *</FormLabel>
              <Input
                type="number"
                {...register('deposit', {
                  required: 'نرخ بیعانه الزامی است',
                  min: { value: 0, message: 'نرخ بیعانه نمی‌تواند منفی باشد' },
                  max: { value: 10000000, message: 'نرخ بیعانه نمی‌تواند بیشتر از 10 میلیون تومان باشد' },
                  validate: (value) => {
                    if (value && !Number.isInteger(parseFloat(value))) {
                      return 'نرخ بیعانه باید عدد صحیح باشد';
                    }
                    return true;
                  }
                })}
                placeholder="مبلغ به تومان"
                min={1000}
                
              />
              <FormErrorMessage>
                {errors.deposit && errors.deposit.message}
              </FormErrorMessage>
            </FormControl>
          </GridItem>
        </Grid>

        <FormControl isInvalid={errors.paymentLink}>
          <FormLabel>لینک پرداخت بیعانه (اختیاری)</FormLabel>
          <Input
            type="url"
            dir="ltr"
            placeholder="https://..."
            {...register('paymentLink', {
              validate: (value) => {
                if (!value || String(value).trim() === '') return true;
                try {
                  const u = new URL(String(value).trim());
                  if (!['http:', 'https:'].includes(u.protocol)) {
                    return 'فقط آدرس http یا https مجاز است';
                  }
                  return true;
                } catch {
                  return 'آدرس معتبر وارد کنید';
                }
              }
            })}
          />
          <FormHelperText>
            اگر خالی باشد، مراجع برای پرداخت به لینک پیش‌فرض کلینیک هدایت می‌شود.
          </FormHelperText>
          <FormErrorMessage>{errors.paymentLink?.message}</FormErrorMessage>
        </FormControl>

        {/* تاریخ پایان نوبت‌دهی (اختیاری؛ بعد از این تاریخ رزرو امکان‌پذیر نیست) */}
        <FormControl isInvalid={errors.appointmentEndDate}>
          <HStack align="flex-end" spacing={2}>
            <Box flex={1}>
              <JalaliDatePicker
                label="تاریخ پایان نوبت‌دهی"
                placeholder="تاریخ پایان نوبت‌دهی را انتخاب کنید"
                value={watch('appointmentEndDate')}
                onChange={(date) => {
                  setValue('appointmentEndDate', date ?? '');
                }}
                isInvalid={!!errors.appointmentEndDate}
                errorMessage={errors.appointmentEndDate?.message}
              />
            </Box>
            <Button
              size="md"
              variant="outline"
              colorScheme="gray"
              onClick={() => setValue('appointmentEndDate', '')}
              isDisabled={!watch('appointmentEndDate')}
            >
              پاک کردن
            </Button>
          </HStack>
          <FormHelperText>
            خالی = آزاد (بدون محدودیت). با «پاک کردن» می‌توانید محدودیت را بردارید.
          </FormHelperText>
        </FormControl>

        {/* بیوگرافی */}
        <FormControl isInvalid={errors.bio}>
          <FormLabel>بیوگرافی</FormLabel>
          <Textarea
            {...register('bio', {
              maxLength: { value: 500, message: 'بیوگرافی نمی‌تواند بیشتر از 500 کاراکتر باشد' },
              pattern: {
                value: /^[\u0600-\u06FF\s\-\(\)\.\,\!\?0-9]+$/,
                message: 'بیوگرافی باید فقط شامل حروف فارسی، اعداد، علائم نگارشی و فاصله باشد'
              }
            })}
            placeholder="توضیحات مختصر درباره مشاور"
            rows={4}
          />
          <FormErrorMessage>
            {errors.bio && errors.bio.message}
          </FormErrorMessage>
        </FormControl>

        {/* وضعیت فعال */}
        <FormControl display="flex" alignItems="center">
          <FormLabel htmlFor="active" mb="0">
            فعال
          </FormLabel>
          <Switch
            id="active"
            {...register('active')}
            defaultChecked={consultant?.active !== false}
          />
          <FormHelperText mr={2}>
            مشاور فعال می‌تواند ملاقات‌ها را دریافت کند
          </FormHelperText>
        </FormControl>
      </VStack>
    </form>
  );
});

export default ConsultantForm; 